import React from "react";
import { apiKirimPengingat, apiPengingatTagihan, formatApiErrorDetail } from "@/lib/api";
import { BellRing, Loader2, MessageCircle, RefreshCw } from "lucide-react";
import * as W from "@/lib/waktu";

// owner 2026-10-15 (TAMBAHAN-OWNER-OKT A8): "Jadwal pengingat hari ini" — unpaid invoices,
// every 2 days, max 2 times, gone once paid (the server decides). One click = the server
// records the reminder (audit) and queues the WhatsApp message; in simulation mode the
// cashier's own WhatsApp opens with the message ready (wa.me).
const rp = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export default function PengingatTagihan() {
  const [d, setD] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [pesan, setPesan] = React.useState(null);
  const load = React.useCallback(async () => {
    try { setD(await apiPengingatTagihan()); } catch (e) { setPesan({ ok: false, t: errText(e) }); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const kirim = async (r) => {
    setBusy(r.nota_id); setPesan(null);
    // open the tab inside the click (a tab opened after waiting for the server is blocked by browsers)
    const tab = d?.mode === "simulasi" ? window.open("", "_blank") : null;
    try {
      const res = await apiKirimPengingat(r.nota_id);
      if (tab && res.wa_me) tab.location.href = res.wa_me; else if (tab) tab.close();
      setPesan({ ok: true, t: `Pengingat ke-${res.ke} untuk ${r.nomor_nota} dicatat${d?.mode === "simulasi" ? " — kirim pesan di WhatsApp yang terbuka" : " dan masuk antrean WhatsApp"}.` });
      await load();
    } catch (e) {
      if (tab) tab.close();
      setPesan({ ok: false, t: errText(e) });
      await load();
    } finally { setBusy(null); }
  };

  if (!d) return null;
  return (
    <section data-testid="pengingat-tagihan" className="rounded-xl border border-soq-oranye/50 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-[15px] font-bold text-soq-biru-tua"><BellRing size={16} className="text-soq-oranye" /> Jadwal pengingat hari ini</h2>
        <span data-testid="pengingat-jumlah" className="rounded-full bg-soq-oranye px-2 py-0.5 text-xs font-bold text-soq-biru-tua">{d.rows.length}</span>
        <span className="text-xs text-slate-500">Faktur belum lunas{d.mulai ? ` sejak ${W.tanggal(d.mulai)}` : ""} · setiap {d.hari} hari · maksimal {d.maks} kali · berhenti otomatis saat lunas</span>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"><RefreshCw size={13} /> Muat ulang</button>
      </div>
      {d.mulai && <p data-testid="pengingat-mulai" className="text-xs text-slate-500">Faktur yang terbit sebelum {W.tanggal(d.mulai)} tidak masuk jadwal ini — ditagih manual.</p>}
      {!d.aktif && <p className="text-sm text-slate-500">Pengingat tagihan dimatikan Owner (menu WhatsApp).</p>}
      {pesan && <p data-testid="pengingat-pesan" className={`text-sm ${pesan.ok ? "text-emerald-600" : "text-red-600"}`}>{pesan.t}</p>}
      {d.aktif && d.rows.length === 0 && <p className="text-sm text-slate-500">Tidak ada pengingat yang perlu dikirim hari ini.</p>}
      {d.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table data-testid="pengingat-table" className="w-full text-sm">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-800">
              <th className="px-3 py-2">Faktur</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Kasir/CS</th>
              <th className="px-3 py-2 text-right">Sisa</th><th className="px-3 py-2">Pengingat</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.nota_id} data-testid={`pengingat-row-${r.nomor_nota}`} className="border-b border-slate-800/60">
                  <td className="px-3 py-2 font-mono text-amber-300">{r.nomor_nota}<span className="block font-sans text-[11px] text-slate-500">faktur {W.tanggal(r.tanggal_faktur)}</span></td>
                  <td className="px-3 py-2">{r.customer}{!r.ada_wa && <span className="block text-[11px] text-red-600">nomor WA kosong / tidak valid</span>}</td>
                  <td className="px-3 py-2 text-xs">{r.cs_nama || "-"}</td>
                  <td className="px-3 py-2 text-right font-mono">{rp(r.sisa)}</td>
                  <td className="px-3 py-2 text-xs">ke-{r.ke} dari {d.maks}{r.terlambat_hari > 0 && <span className="block text-amber-600">terlambat {r.terlambat_hari} hari</span>}
                    {r.terakhir && <span className="block text-slate-500">terakhir {W.waktu(r.terakhir)}</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`pengingat-kirim-${r.nomor_nota}`} disabled={busy === r.nota_id || !r.ada_wa} onClick={() => kirim(r)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-40">
                      {busy === r.nota_id ? <Loader2 size={13} className="animate-spin" /> : <MessageCircle size={13} />} Kirim WA
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
